import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { formatINR } from "@/lib/format";
import { useCities } from "@/lib/useCities";

export const PRICE_CAP = 100000000; // ₹10 Cr
const BHK_OPTS = ["1", "2", "3", "4", "5"];
const COMMERCIAL_OPTS = [["shop", "Shop"], ["office", "Office Space"], ["showroom", "Showroom"]];
const POSSESSION_OPTS = [["ready", "RTMI"], ["under_construction", "Under Construction"]];
const FLAG_OPTS = [["verified", "Verified"], ["rera_registered", "RERA Registered"], ["featured", "Featured"], ["rtmi", "RTMI"]];

function Group({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">{label}</div>
      {children}
    </div>
  );
}

function CheckRow({ tid, label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2.5 py-1.5 cursor-pointer text-sm text-slate-700 hover:text-blue-700 transition-colors" data-testid={tid}>
      <Checkbox checked={checked} onCheckedChange={onChange} />
      <span>{label}</span>
    </label>
  );
}

/**
 * Shared Projects filter panel — the single projects filter system, reused by
 * the Projects listing page (sidebar/drawer) and the Enquiry page.
 * Stateless: the parent owns params/keyword and supplies update/search/clearAll.
 * Multi-select values are comma-joined into the existing query params.
 */
export default function ProjectFilters({ params, update, keyword, setKeyword, search, clearAll }) {
  const cityOptions = useCities();
  const isCommercial = params.category === "commercial";
  const priceMin = Number(params.price_min || 0);
  const priceMax = Number(params.price_max || PRICE_CAP);

  const listVal = (k) => (params[k] ? String(params[k]).split(",") : []);
  const toggleList = (k, v) => {
    const cur = listVal(k);
    const next = cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v];
    update(k, next.join(","));
  };
  const flagOn = (k) => params[k] === "1" || params[k] === "true";

  const activeCount = Object.entries(params).filter(([k, v]) => v && !["category", "q", "sort", "page"].includes(k)).length + (params.q ? 1 : 0);

  return (
    <div className="space-y-5" data-testid="project-filters">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-900">Filters{activeCount > 0 ? ` (${activeCount})` : ""}</span>
        {activeCount > 0 && clearAll && (
          <button type="button" onClick={clearAll} data-testid="pf-clear-all" className="text-xs font-medium text-blue-600 hover:text-blue-800 inline-flex items-center gap-1">
            <X size={12} weight="bold" /> Clear all
          </button>
        )}
      </div>

      <Group label="Keyword">
        <Input data-testid="pf-keyword" value={keyword} onChange={e => setKeyword(e.target.value)}
          onKeyDown={e => e.key === "Enter" && search()} placeholder="Project or developer…" className="h-11 rounded-lg border-slate-300" />
      </Group>

      <Group label="Possession">
        {POSSESSION_OPTS.map(([v, l]) => (
          <CheckRow key={v} tid={`pf-possession-${v}`} label={l} checked={listVal("construction_status").includes(v)} onChange={() => toggleList("construction_status", v)} />
        ))}
      </Group>

      <Group label="City">
        <Select value={params.city || ""} onValueChange={v => update("city", v === "any" ? "" : v)}>
          <SelectTrigger data-testid="pf-city" className="h-11 rounded-lg border-slate-300"><SelectValue placeholder="Any city" /></SelectTrigger>
          <SelectContent><SelectItem value="any">Any city</SelectItem>{cityOptions.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </Group>

      {isCommercial ? (
        <Group label="Commercial Type">
          {COMMERCIAL_OPTS.map(([v, l]) => (
            <CheckRow key={v} tid={`pf-config-${v}`} label={l} checked={listVal("config").includes(v)} onChange={() => toggleList("config", v)} />
          ))}
        </Group>
      ) : (
        <Group label="BHK">
          {BHK_OPTS.map(n => (
            <CheckRow key={n} tid={`pf-bhk-${n}`} label={n === "5" ? "5+ BHK" : `${n} BHK`} checked={listVal("bhk").includes(n)} onChange={() => toggleList("bhk", n)} />
          ))}
        </Group>
      )}

      <Group label="Price">
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span data-testid="pf-price-min-label">{priceMin > 0 ? formatINR(priceMin) : "No min"}</span>
          <span className="text-blue-600 font-bold" data-testid="pf-price-max-label">{priceMax >= PRICE_CAP ? "Any" : formatINR(priceMax)}</span>
        </div>
        <Slider data-testid="pf-price" value={[priceMin, priceMax]} min={0} max={PRICE_CAP} step={500000}
          onValueChange={([a, b]) => { update("price_min", a > 0 ? String(a) : ""); update("price_max", b >= PRICE_CAP ? "" : String(b)); }} className="py-3" />
        <div className="grid grid-cols-2 gap-2 mt-1">
          <Input data-testid="pf-price-min" type="number" min={0} value={params.price_min || ""} placeholder="Min ₹"
            onChange={e => update("price_min", e.target.value)} className="h-10 rounded-lg border-slate-300 text-sm" />
          <Input data-testid="pf-price-max" type="number" min={0} value={params.price_max || ""} placeholder="Max ₹"
            onChange={e => update("price_max", e.target.value)} className="h-10 rounded-lg border-slate-300 text-sm" />
        </div>
      </Group>

      <Group label="Highlights">
        {FLAG_OPTS.map(([k, l]) => (
          <CheckRow key={k} tid={`pf-flag-${k}`} label={l} checked={flagOn(k)} onChange={() => update(k, flagOn(k) ? "" : "1")} />
        ))}
      </Group>

      <button onClick={search} data-testid="pf-search" className="h-11 w-full px-6 bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors shadow-md shadow-blue-500/25">
        <MagnifyingGlass size={16} weight="bold" /> Search
      </button>
    </div>
  );
}
